// Shared RANK helpers (roles[]-retirement Wave 1-B).
//
// The UI shows a user's RANK (the 5-rung capability ladder) instead of legacy role-name strings.
// `rankFromRole` mirrors the backend mig-139 crosswalk (role -> rank, taking the HIGHEST rank over a
// multi-role roles[]). Display-only: it never drives an access decision — the server is authoritative.
// Prefer a payload's own `u.rank` when present (e.g. GET /users, /me); derive only as a fallback for
// lists that don't yet carry the column (e.g. the platform team/reports payloads).
import React from 'react';

const RANK_ORDER = ['Viewer', 'Member', 'Lead', 'Manager', 'Admin'] as const;

function rankOfOneRole(role: string): string {
  if (/_admin$/.test(role)) return 'Admin';
  if (/_manager$/.test(role)) return 'Manager'; // incl. platform_account_manager
  if (/_team_lead$/.test(role)) return 'Lead';
  if (/(_operator|_coordinator|_clinician)$/.test(role)) return 'Member';
  return 'Viewer'; // supervisor / staff / doctor — clinical identity is the license, not the rank
}

/** Highest rank over a role / roles[] (mirrors mig-139). Returns '' when there is nothing to derive. */
export function rankFromRole(role?: string | null, roles?: string[] | null): string {
  const list = ((roles && roles.length ? roles : role ? [role] : []) as string[]).filter(Boolean);
  if (!list.length) return '';
  return list
    .map(rankOfOneRole)
    .reduce((a, b) => (RANK_ORDER.indexOf(b as any) > RANK_ORDER.indexOf(a as any) ? b : a));
}

/** Convenience: use the payload's own rank if present, else derive from role/roles. */
export function rankOf(u: { rank?: string | null; role?: string | null; roles?: string[] | null }): string {
  return (u.rank as string) || rankFromRole(u.role, u.roles);
}

/** The shared rank pill. */
export function RankBadge({ rank }: { rank?: string | null }) {
  if (!rank) return <span className="text-xs text-gray-300">—</span>;
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
      {rank}
    </span>
  );
}
